import React, { useState, useEffect } from "react";
import MetaTags from "react-meta-tags";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import "./GreyBoxTesting.scss";

const Icon = ({ d, size = 16, stroke = "currentColor", width = 2, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    {[].concat(d).map((p, i) => (
      <path key={i} d={p} />
    ))}
  </svg>
);

const ICONS = {
  layers: "m12 2 10 5-10 5L2 7zM2 17l10 5 10-5M2 12l10 5 10-5",
  shield:
    "M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z",
  doc: ["M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z", "M14 2v4a2 2 0 0 0 2 2h4"],
  globe: ["M2 12h20", "M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20"],
  check: "M20 6 9 17l-5-5",
  send: ["M22 2 11 13", "M22 2l-7 20-4-9-9-4z"],
  chevron: "m9 18 6-6-6-6",
  alert: ["M12 8v4", "M12 16h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20"],
  x: ["M18 6 6 18", "M6 6l12 12"],
  clock: ["M12 6v6l4 2", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20"],
  refresh: ["M21 12a9 9 0 1 1-3-6.7L21 8", "M21 3v5h-5"],
  inbox: ["M22 12h-6l-2 3h-4l-2-3H2", "M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.7 1.1"],
  mail: ["M22 7 13 12.6a2 2 0 0 1-2 0L2 7", "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2"],
};

const STEP_TITLES = ["Target Application", "Test Accounts", "Scope & Schedule", "Contact & Notes", "Review"];
const SECTION_TITLES = [
  "1. TARGET APPLICATION",
  "2. TEST ACCOUNTS & CREDENTIAL ACCESS",
  "3. SCOPE DESCRIPTION & SCHEDULE",
  "4. CONTACT & ADDITIONAL NOTES",
  "5. REVIEW REQUEST",
];

const ACCESS_METHODS = [
  {
    value: "client_provides",
    title: "I will provide credentials",
    desc: "You will securely share test credentials with our team during engagement kickoff.",
  },
  {
    value: "request_creation",
    title: "Please create test accounts",
    desc: "Our analysts will register new test accounts using Xiarch domain email addresses.",
  },
];

const STATUS_STYLES = {
  Submitted: "amber",
  "In Review": "blue",
  Scheduled: "teal",
  Completed: "green",
};

const MAX_ACCOUNTS = 10;

const formatDate = (d) => (d ? d.split("-").reverse().join("-") : "");

const GreyBoxTesting = () => {
  const token = localStorage.getItem("token");
  const orgID = localStorage.getItem("orgID");
  const userID = localStorage.getItem("userID");

  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
  const theme = layoutMode === "light" ? "light" : "dark";

  // User details
  const [userEmail, setUserEmail] = useState("");

  // Form states
  const [targetApplication, setTargetApplication] = useState("");
  const [numberOfAccounts, setNumberOfAccounts] = useState(1);
  const [accountRoles, setAccountRoles] = useState([""]);
  const [accessMethod, setAccessMethod] = useState("client_provides");
  const [scopeDescription, setScopeDescription] = useState("");
  const [testingWindowStart, setTestingWindowStart] = useState("");
  const [testingWindowEnd, setTestingWindowEnd] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  // Wizard state
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  // UI / Async states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState(null);
  const [pastRequests, setPastRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);

  // Fetch past grey-box requests
  const fetchRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}greybox/requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "organization-id": orgID || "",
        },
        body: JSON.stringify({ token, orgID }),
      });
      const data = await resp.json();
      if (data && data.status && Array.isArray(data.requests)) {
        setPastRequests(data.requests);
      }
    } catch (err) {
      console.error("Failed to load grey-box requests:", err);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Initial auth check & profile fetch
  useEffect(() => {
    async function checkAuthAndProfile() {
      try {
        const authResp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const authData = await authResp.json();
        if (!authData.status) {
          window.location.href = "/login";
          return;
        }

        // Fetch user profile for contact email
        if (userID) {
          const profileResp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}user-profile`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userCheck: userID }),
          });
          const profileData = await profileResp.json();
          if (profileData && profileData.user && profileData.user.Email) {
            setUserEmail(profileData.user.Email);
            setContactEmail(profileData.user.Email);
          }
        }
      } catch (e) {
        console.error("Auth/Profile check error:", e);
      }
    }

    checkAuthAndProfile();
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep accountRoles length in sync with numberOfAccounts
  const handleAccountsCountChange = (count) => {
    const num = Math.min(MAX_ACCOUNTS, Math.max(1, parseInt(count, 10) || 1));
    setNumberOfAccounts(num);
    setAccountRoles((prev) => {
      const newRoles = [...prev];
      while (newRoles.length < num) newRoles.push("");
      newRoles.splice(num);
      return newRoles;
    });
  };

  const handleRoleChange = (index, value) => {
    setAccountRoles((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const resetForm = () => {
    setTargetApplication("");
    setNumberOfAccounts(1);
    setAccountRoles([""]);
    setAccessMethod("client_provides");
    setScopeDescription("");
    setTestingWindowStart("");
    setTestingWindowEnd("");
    setContactEmail(userEmail);
    setAdditionalNotes("");
    setSubmitFeedback(null);
    setStep(0);
  };

  // Per-step completeness (drives the step checkmarks + Send button)
  const stepOk = [
    !!targetApplication.trim(),
    accountRoles.every((r) => r.trim()),
    !!scopeDescription.trim(),
    !!contactEmail.trim() && contactEmail.includes("@"),
  ];
  const ready = stepOk.every(Boolean);

  // Form Submission (same validation + payload as before)
  const handleSubmit = async () => {
    setSubmitFeedback(null);

    if (!targetApplication.trim()) {
      setSubmitFeedback({ type: "danger", message: "Target Application/URL is required." });
      setStep(0);
      return;
    }
    if (!accountRoles.every((r) => r.trim())) {
      setSubmitFeedback({ type: "danger", message: "Please specify a role for every test account." });
      setStep(1);
      return;
    }
    if (!scopeDescription.trim()) {
      setSubmitFeedback({ type: "danger", message: "Scope Description is required." });
      setStep(2);
      return;
    }
    if (!contactEmail.trim() || !contactEmail.includes("@")) {
      setSubmitFeedback({ type: "danger", message: "Please provide a valid Contact Email." });
      setStep(3);
      return;
    }
    if (testingWindowStart && testingWindowEnd) {
      if (new Date(testingWindowStart) > new Date(testingWindowEnd)) {
        setSubmitFeedback({
          type: "danger",
          message: "Testing window Start Date cannot be after End Date.",
        });
        setStep(2);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        token,
        orgID,
        targetApplication: targetApplication.trim(),
        numberOfAccounts,
        accountRoles: accountRoles.map((r) => r.trim()),
        accessMethod,
        scopeDescription: scopeDescription.trim(),
        testingWindowStart: testingWindowStart || null,
        testingWindowEnd: testingWindowEnd || null,
        contactEmail: contactEmail.trim(),
        additionalNotes: additionalNotes.trim(),
      };

      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}greybox/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "organization-id": orgID || "",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok && result.status) {
        setSubmitted(contactEmail.trim());
        fetchRequests();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setSubmitFeedback({
          type: "danger",
          message: result.error || "Failed to submit request. Please try again.",
        });
      }
    } catch (err) {
      console.error("Submission error:", err);
      setSubmitFeedback({
        type: "danger",
        message: "Network error while submitting request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startNewRequest = () => {
    setSubmitted(false);
    resetForm();
  };

  const windowSummary =
    testingWindowStart || testingWindowEnd
      ? `${formatDate(testingWindowStart) || "Any"} → ${formatDate(testingWindowEnd) || "Any"}`
      : "";

  const reviewItems = [
    { title: "Target Application", value: targetApplication.trim(), mono: true },
    {
      title: "Test Accounts & Credential Access",
      value: `${numberOfAccounts} ${numberOfAccounts > 1 ? "Accounts" : "Account"} · ${
        accessMethod === "client_provides" ? "I will provide credentials" : "Please create test accounts"
      }`,
    },
    {
      title: "Scope Description & Schedule",
      value: windowSummary
        ? `${scopeDescription.trim().slice(0, 60)}${scopeDescription.trim().length > 60 ? "…" : ""} · ${windowSummary}`
        : scopeDescription.trim(),
    },
    { title: "Contact & Additional Notes", value: contactEmail.trim(), mono: true },
  ];

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="gb-field">
            <label htmlFor="gbTarget">
              Target Application / URL <span className="req">*</span>
            </label>
            <div className="gb-input-icon">
              <Icon d={ICONS.globe} size={15} stroke="var(--fg-tertiary)" />
              <input
                id="gbTarget"
                className="gb-input mono"
                placeholder="e.g. https://portal.company.com or Core Banking Web Application"
                value={targetApplication}
                onChange={(e) => setTargetApplication(e.target.value)}
                autoFocus
              />
            </div>
            <span className="gb-help">
              Specify the fully qualified URL, domain name, or application identifier to be assessed.
            </span>
          </div>
        );

      case 1:
        return (
          <>
            <div className="gb-two-col">
              <div className="gb-field">
                <label>
                  Number of Test User Accounts Needed <span className="req">*</span>
                </label>
                <div className="gb-stepper">
                  <button
                    type="button"
                    aria-label="Fewer accounts"
                    onClick={() => handleAccountsCountChange(numberOfAccounts - 1)}
                    disabled={numberOfAccounts <= 1}
                  >
                    −
                  </button>
                  <span>
                    {numberOfAccounts} {numberOfAccounts > 1 ? "Accounts" : "Account"}
                  </span>
                  <button
                    type="button"
                    aria-label="More accounts"
                    onClick={() => handleAccountsCountChange(numberOfAccounts + 1)}
                    disabled={numberOfAccounts >= MAX_ACCOUNTS}
                  >
                    +
                  </button>
                </div>
                <span className="gb-help">
                  Having multiple user roles enables privilege escalation &amp; authorization testing.
                </span>
              </div>

              <div className="gb-field">
                <label>
                  Role per Account <span className="req">*</span>
                </label>
                <div className="gb-roles">
                  {accountRoles.map((role, idx) => (
                    <div className="gb-addon" key={idx}>
                      <span>Account {idx + 1}</span>
                      <input
                        placeholder={
                          idx === 0
                            ? "e.g. Administrator / Tenant Admin"
                            : idx === 1
                            ? "e.g. Regular User / Employee"
                            : `Role for Account ${idx + 1}`
                        }
                        value={role}
                        onChange={(e) => handleRoleChange(idx, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <span className="gb-help">
                  Specify the privileges or role type associated with each needed test user account.
                </span>
              </div>
            </div>

            <div className="gb-field">
              <label>
                Access Method <span className="req">*</span>
              </label>
              <div className="gb-methods">
                {ACCESS_METHODS.map((m) => {
                  const sel = accessMethod === m.value;
                  return (
                    <button
                      type="button"
                      key={m.value}
                      className={`gb-method${sel ? " selected" : ""}`}
                      onClick={() => setAccessMethod(m.value)}
                    >
                      <span className="radio">
                        <span className="dot" />
                      </span>
                      <span className="text">
                        <span className="title">{m.title}</span>
                        <span className="desc">{m.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <div className="gb-field">
              <label htmlFor="gbScope">
                Scope Description <span className="req">*</span>
              </label>
              <textarea
                id="gbScope"
                className="gb-input gb-textarea"
                rows={5}
                placeholder="Detail the modules, critical business flows, API endpoints, or third-party integrations in scope. Note any specific testing restrictions (e.g. avoid stress testing or payment gateway triggers)."
                value={scopeDescription}
                onChange={(e) => setScopeDescription(e.target.value)}
              />
              <span className="gb-count">{scopeDescription.length} characters</span>
            </div>
            <div className="gb-two-col">
              <div className="gb-field">
                <label htmlFor="gbStart">
                  Preferred Testing Window Start <span className="opt">(Optional)</span>
                </label>
                <input
                  id="gbStart"
                  type="date"
                  className="gb-input"
                  value={testingWindowStart}
                  onChange={(e) => setTestingWindowStart(e.target.value)}
                />
                <span className="gb-help">Earliest date testing may commence.</span>
              </div>
              <div className="gb-field">
                <label htmlFor="gbEnd">
                  Preferred Testing Window End <span className="opt">(Optional)</span>
                </label>
                <input
                  id="gbEnd"
                  type="date"
                  className="gb-input"
                  value={testingWindowEnd}
                  min={testingWindowStart || undefined}
                  onChange={(e) => setTestingWindowEnd(e.target.value)}
                />
                <span className="gb-help">Expected target completion date.</span>
              </div>
            </div>
          </>
        );

      case 3:
        return (
          <div className="gb-two-col">
            <div className="gb-field">
              <label htmlFor="gbEmail">
                Contact Email <span className="req">*</span>
              </label>
              <input
                id="gbEmail"
                type="email"
                className="gb-input"
                placeholder="security-lead@company.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              <span className="gb-help">
                Our security team will communicate and confirm schedule via this email.
              </span>
            </div>
            <div className="gb-field">
              <label htmlFor="gbNotes">
                Additional Notes / Instructions <span className="opt">(Optional)</span>
              </label>
              <textarea
                id="gbNotes"
                className="gb-input gb-textarea"
                rows={3}
                placeholder="e.g. IP whitelisting details, MFA bypass codes, VPN requirements, staging environment URL..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="gb-review">
            {reviewItems.map((item, i) => (
              <div className="gb-review-row" key={item.title}>
                <span className={`gb-dot${stepOk[i] ? " ok" : ""}`}>{stepOk[i] ? "✓" : i + 1}</span>
                <span className="body">
                  <span className="title">{item.title}</span>
                  <span className={`value${item.mono && item.value ? " mono" : ""}`}>
                    {item.value || "Not provided"}
                  </span>
                </span>
                <button type="button" className="gb-link" onClick={() => setStep(i)}>
                  Edit
                </button>
              </div>
            ))}
            {!ready && (
              <div className="gb-hint">
                <Icon d={ICONS.alert} size={14} />
                Complete all required fields to send the request.
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <React.Fragment>
      <div className="page-content greybox-root" data-theme={theme}>
        <MetaTags>
          <title>Grey Box Testing | Glogix</title>
        </MetaTags>

        <div className="gb-container">
          {/* Header */}
          <div className="gb-header">
            <div className="gb-breadcrumb">
              <Link to="/dashboard">Features</Link>
              <Icon d={ICONS.chevron} size={12} />
              <span className="current">Grey Box Testing</span>
            </div>
            <h1>Grey Box Testing</h1>
          </div>

          {/* Intro banner */}
          <section className="gb-card gb-banner">
            <span className="gb-banner-icon">
              <Icon d={ICONS.layers} size={24} />
            </span>
            <div className="gb-banner-text">
              <span className="title">Manual Grey-Box Penetration Testing</span>
              <span className="desc">
                Comprehensive authenticated security assessment conducted directly by Xiarch security analysts.
                Submit your application scope and credential requirements below.
              </span>
            </div>
            <span className="gb-chip">
              <Icon d={ICONS.shield} size={13} stroke="var(--blue-500)" />
              Expert Human Assessment
            </span>
          </section>

          {/* Feedback */}
          {submitFeedback && (
            <div className={`gb-alert ${submitFeedback.type}`}>
              <Icon d={submitFeedback.type === "success" ? ICONS.check : ICONS.alert} size={16} />
              <span>{submitFeedback.message}</span>
              <button type="button" aria-label="Dismiss" onClick={() => setSubmitFeedback(null)}>
                <Icon d={ICONS.x} size={14} />
              </button>
            </div>
          )}

          {submitted ? (
            <section className="gb-card gb-success">
              <span className="gb-success-icon">
                <Icon d={ICONS.check} size={24} width={2.5} />
              </span>
              <span className="title">Request sent</span>
              <span className="desc">
                Our security team will communicate and confirm schedule via {submitted}.
              </span>
              <button type="button" className="gb-btn-secondary" onClick={startNewRequest}>
                New request
              </button>
            </section>
          ) : (
            <section className="gb-card">
              {/* Step tabs */}
              <div className="gb-steps">
                {STEP_TITLES.map((title, i) => {
                  const active = step === i;
                  const ok = i < 4 && stepOk[i];
                  return (
                    <button
                      type="button"
                      key={title}
                      className={`gb-step${active ? " active" : ""}`}
                      onClick={() => setStep(i)}
                    >
                      <span className={`gb-dot${ok ? " ok" : active ? " current" : ""}`}>
                        {ok ? "✓" : i + 1}
                      </span>
                      <span className="labels">
                        <span className="n">Step {i + 1}</span>
                        <span className="t">{title}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Step body */}
              <div className="gb-body">
                <div className="gb-section-title">
                  <Icon d={ICONS.doc} size={16} stroke="var(--blue-500)" />
                  <span>{SECTION_TITLES[step]}</span>
                </div>
                {renderStep()}
              </div>

              {/* Footer */}
              <div className="gb-footer">
                <button type="button" className="gb-link muted" onClick={resetForm}>
                  Reset Form
                </button>
                <div className="gb-footer-actions">
                  {step > 0 && (
                    <button type="button" className="gb-btn-secondary" onClick={() => setStep(step - 1)}>
                      Back
                    </button>
                  )}
                  {step < 4 ? (
                    <button type="button" className="gb-btn-primary" onClick={() => setStep(step + 1)}>
                      {step === 3 ? "Review" : "Next"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="gb-btn-primary"
                      onClick={handleSubmit}
                      disabled={!ready || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="gb-spinner" />
                          Submitting Request...
                        </>
                      ) : (
                        <>
                          <Icon d={ICONS.send} size={14} />
                          Send Request
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Request history */}
          <section className="gb-card">
            <div className="gb-card-head">
              <div className="left">
                <Icon d={ICONS.clock} size={16} stroke="var(--blue-500)" />
                <span className="title">Organization Request History</span>
                <span className="gb-count-badge">{pastRequests.length}</span>
              </div>
              <button
                type="button"
                className="gb-btn-secondary small"
                onClick={fetchRequests}
                disabled={isLoadingRequests}
              >
                <Icon d={ICONS.refresh} size={13} style={isLoadingRequests ? { animation: "gb-spin 0.8s linear infinite" } : undefined} />
                Refresh
              </button>
            </div>

            {isLoadingRequests && pastRequests.length === 0 ? (
              <div className="gb-empty">
                <span className="gb-spinner large" />
                <span className="desc">Loading request history...</span>
              </div>
            ) : pastRequests.length === 0 ? (
              <div className="gb-empty">
                <span className="gb-empty-icon">
                  <Icon d={ICONS.inbox} size={24} />
                </span>
                <span className="title">No Grey-Box Requests Yet</span>
                <span className="desc">
                  Submit the intake form above to request your first manual grey-box assessment.
                </span>
              </div>
            ) : (
              <div className="gb-table-wrap">
                <table className="gb-table">
                  <thead>
                    <tr>
                      <th>Target Application</th>
                      <th>Status</th>
                      <th>Accounts &amp; Roles</th>
                      <th>Access Method</th>
                      <th>Testing Window</th>
                      <th>Contact</th>
                      <th>Submitted On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastRequests.map((req, idx) => {
                      const status = req.status || "Submitted";
                      const roles = req.accountRoles || [];
                      return (
                        <tr key={req._id || idx}>
                          <td>
                            <span className="gb-target" title={req.targetApplication}>
                              {req.targetApplication}
                            </span>
                            <span className="gb-sub">By: {req.requestedBy || "Client User"}</span>
                          </td>
                          <td>
                            <span className={`gb-status ${STATUS_STYLES[status] || "slate"}`}>{status}</span>
                          </td>
                          <td>
                            <span className="gb-tag">
                              {req.numberOfAccounts || (roles.length || 1)} Acc(s)
                            </span>
                            {roles.length > 0 && (
                              <span className="gb-sub">
                                {roles.slice(0, 2).join(", ")}
                                {roles.length > 2 ? ` +${roles.length - 2} more` : ""}
                              </span>
                            )}
                          </td>
                          <td>
                            {req.accessMethod === "client_provides"
                              ? "Client provides credentials"
                              : "Create test accounts"}
                          </td>
                          <td>
                            {req.testingWindowStart || req.testingWindowEnd ? (
                              <span className="gb-mono-sm">
                                {req.testingWindowStart
                                  ? new Date(req.testingWindowStart).toLocaleDateString()
                                  : "Any"}{" "}
                                →{" "}
                                {req.testingWindowEnd ? new Date(req.testingWindowEnd).toLocaleDateString() : "Any"}
                              </span>
                            ) : (
                              <span className="gb-tag muted">Flexible</span>
                            )}
                          </td>
                          <td>
                            <a href={`mailto:${req.contactEmail}`} className="gb-mail">
                              <Icon d={ICONS.mail} size={13} />
                              {req.contactEmail}
                            </a>
                          </td>
                          <td className="gb-mono-sm">
                            {req.createdAt
                              ? new Date(req.createdAt).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </React.Fragment>
  );
};

export default GreyBoxTesting;
